import { useEffect, useState } from "react";
import { extendWindowTph } from "utils/windowTph";

const EVENT_NAME = 'sessionStorageUpdated';
const FLAG_PREFIX = 'tph_feature:';

export type SessionFlag =
    /** Mimick the role of the provider (e.g. 'EAP', 'CI', 'MIXED', 'NONE') */
    | 'provider-role'
    /** Force the app region (e.g. 'en-AU', 'en-CA', 'en-GB', 'en-US', 'fr-CA') */
    | 'forced-region'
    /** Enable critical incident feature for providers that don't meet the contractedServices requirement (e.g. true. false) */
    | 'enable-critical-incident'
    /** Enable emergency contact feature by setting the date (e.g. '2025-01-01') */
    | 'emergency-contact-launch-date'
    /** Control the position of label in the LabelValue component (e.g. 'top', 'bottom') */
    | 'label-position'
    /** Enable mock mode for using the mock server locally (e.g. true, false) */
    | 'use-mock-server'
    /** Set the post-login path (e.g. '/clients', '/profile') */
    | 'expired-url'
    /** Allow use of fake inline data to test the CCC Notes feature (e.g. true, false) */
    | 'use-fake-ccc-notes'
    /** Allow use of fake inline data to test the Provider Bio section (e.g. true, false) */
    | 'use-fake-provider-bio';

export function useSessionFlag<T = any>(flagName: SessionFlag, defaultValue?: T) {
    const [flagValue, setFlagValue] = useState<T>(getSessionFlag(flagName, defaultValue));
    // if a flag changes via the setSessionFlag() method, we refetch the value
    function flagChangeHandler(event: CustomEvent<FlagEntry<T>>) {
        if(event.detail.name === flagName) {
            setFlagValue(event.detail.value);
        }
    }
    useEffect(() => {
        window.addEventListener(EVENT_NAME, flagChangeHandler as EventListener);
        return () => window.removeEventListener(EVENT_NAME, flagChangeHandler as EventListener)
    }, []);

    return flagValue;
}

export function useSessionFlags() {
    const [sessionFlags, setSessionFlags] = useState<Record<SessionFlag, any>>(getSessionFlags());
    // if any flag changes, we refetch all
    function flagsChangeHandler() {
        let newValue = getSessionFlags();
        setSessionFlags(newValue);
    }
    useEffect(() => {
        window.addEventListener(EVENT_NAME, flagsChangeHandler as EventListener);
        return () => window.removeEventListener(EVENT_NAME, flagsChangeHandler as EventListener)
    }, []);
    return sessionFlags;
}

function getSessionFlags() {
    return Object.keys(sessionStorage)
                .filter(key => key.startsWith(FLAG_PREFIX))
                .map(key => ({ [`${key.substring(FLAG_PREFIX.length)}`]: getSessionFlag(key.substring(FLAG_PREFIX.length) as SessionFlag)}))
                .reduce((acc, curr) => ({ ...acc, ...curr }), {}) as Record<SessionFlag, any>;
}

export function getSessionFlag<T = any>(flagName: SessionFlag, defaultValue?: T) {
    let flagValue: T;
    try {
        flagValue = JSON.parse(sessionStorage.getItem(`${FLAG_PREFIX}${flagName}`)!) ?? defaultValue;
    } catch (err) {
        console.info(err);
        flagValue = defaultValue!;
    }
    return flagValue;
}

export function clearSessionFlag(flagName: SessionFlag) {
    sessionStorage.removeItem(`${FLAG_PREFIX}${flagName}`);
}

export function clearAllSessionFlags() {
    Object.keys(sessionStorage)
            .filter(key => key.startsWith(FLAG_PREFIX))
            .map(key => sessionStorage.removeItem(key))
}

type FlagEntry<T = any> = {
    name: SessionFlag
    value: T
}

export function setSessionFlag<T = any>(flagName: SessionFlag, value: T) {
    console.info(`🥣 updating session item(${flagName})`, value);
    sessionStorage.setItem(`${FLAG_PREFIX}${flagName}`, JSON.stringify(value));
    const SessionStorageUpdated = new CustomEvent<FlagEntry<T>>(EVENT_NAME, { detail: { name: flagName, value } });
    window.dispatchEvent(SessionStorageUpdated);
}

extendWindowTph(base => ({ ...base, setSessionFlag, getSessionFlags, clearAllSessionFlags }));
